// Update StorySidebar.jsx with story playback functionality
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ChevronLeft, ChevronRight, Play, Pause, 
  Volume2, VolumeX, Settings, Maximize2,
  RotateCcw, SkipForward 
} from 'lucide-react';

export const StorySidebar = ({ 
  story, 
  mediaItems, 
  isOpen, 
  onClose, 
  onFullScreenPlay, // NEW: Callback to open full story player
  position = 'right',
  width = 'normal'
}) => {
  // All hooks first
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasSound, setHasSound] = useState(true);
  const [progress, setProgress] = useState(0);
  
  const playIntervalRef = useRef(null);
  const audioRef = useRef(null);

  // Compute story media
  const storyMedia = story?.mediaSequence 
    ? story.mediaSequence
        .map(id => mediaItems?.find(item => item.id === id))
        .filter(Boolean)
    : [];

  // Get slide duration (per-slide or default)
  const getSlideDuration = (slideIndex) => {
    const mediaId = story?.mediaSequence?.[slideIndex];
    if (story?.slideDurations?.[mediaId]) {
      return story.slideDurations[mediaId];
    }
    return story?.defaultDuration || 5000; // Default 5 seconds
  };

  // Auto-play logic
  useEffect(() => {
    if (isPlaying && storyMedia.length > 0) {
      const currentDuration = getSlideDuration(currentSlide);
      
      playIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 100; // Update every 100ms
          
          if (newProgress >= currentDuration) {
            // Move to next slide
            setCurrentSlide(prevSlide => {
              const nextSlide = (prevSlide + 1) % storyMedia.length;
              
              // If we've completed the story, stop playing
              if (nextSlide === 0 && prevSlide === storyMedia.length - 1) {
                setIsPlaying(false);
                return 0;
              }
              
              return nextSlide;
            });
            return 0; // Reset progress
          }
          
          return newProgress;
        });
      }, 100);
    } else {
      clearInterval(playIntervalRef.current);
    }

    return () => clearInterval(playIntervalRef.current);
  }, [isPlaying, currentSlide, storyMedia.length, story]);

  // Reset when story changes
  useEffect(() => {
    if (story) {
      setCurrentSlide(0);
      setProgress(0);
      setIsPlaying(false); // Start paused, let user play
    }
  }, [story?.id]);

  // Handle background music
  useEffect(() => {
    if (story?.backgroundMusic && audioRef.current) {
      audioRef.current.src = story.backgroundMusic.url;
      audioRef.current.loop = true;
      audioRef.current.volume = hasSound ? 0.3 : 0; // Lower volume for background
      
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [story?.backgroundMusic, isPlaying, hasSound]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(playIntervalRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Early returns after hooks
  if (!story) {
    return null;
  }

  if (storyMedia.length === 0) {
    return null;
  }

  const getWidthClass = () => {
    switch (width) {
      case 'narrow': return 'w-80';
      case 'wide': return 'w-[500px]';
      default: return 'w-96';
    }
  };

  const getPositionClasses = () => {
    if (position === 'left') {
      return {
        container: 'left-0',
        transform: isOpen ? 'translate-x-0' : '-translate-x-full'
      };
    }
    return {
      container: 'right-0',
      transform: isOpen ? 'translate-x-0' : 'translate-x-full'
    };
  };

  const positionClasses = getPositionClasses();
  const currentSlideDuration = getSlideDuration(currentSlide);
  const progressPercentage = (progress / currentSlideDuration) * 100;

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const restartStory = () => {
    setCurrentSlide(0);
    setProgress(0);
    setIsPlaying(true);
  };

  const openFullScreen = () => {
    setIsPlaying(false); // Pause sidebar playback
    onClose(); // Close sidebar
    
    // Open full story player with current state
    setTimeout(() => {
      onFullScreenPlay?.(story, {
        startSlide: currentSlide,
        autoPlay: true
      });
    }, 300);
  };

  return (
    <>
      {/* Background Music Audio */}
      {story.backgroundMusic && (
        <audio ref={audioRef} preload="metadata" />
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 ${positionClasses.container} h-full ${getWidthClass()} bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${positionClasses.transform}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{story.title || 'Untitled Story'}</h3>
            <p className="text-sm text-purple-100 truncate">
              {storyMedia.length} photos • {Math.round((currentSlideDuration / 1000) * 10) / 10}s each
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Story Content */}
        <div className="flex-1 flex flex-col h-full">
          {/* Main Media Display */}
          <div className="relative bg-black">
            <div className="aspect-video">
              {storyMedia[currentSlide] && (
                <img
                  src={storyMedia[currentSlide].url}
                  alt={storyMedia[currentSlide].title || `Slide ${currentSlide + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {/* Play/Pause Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => goToSlide(Math.max(0, currentSlide - 1))}
                  disabled={currentSlide === 0}
                  className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                <button
                  onClick={togglePlayPause}
                  className="p-4 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-all"
                >
                  {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
                </button>

                <button
                  onClick={() => goToSlide(Math.min(storyMedia.length - 1, currentSlide + 1))}
                  disabled={currentSlide === storyMedia.length - 1}
                  className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0">
              {/* Overall Story Progress */}
              <div className="h-1 bg-black bg-opacity-30">
                <div 
                  className="h-full bg-white bg-opacity-60 transition-all duration-300"
                  style={{ width: `${((currentSlide) / storyMedia.length) * 100}%` }}
                />
              </div>
              
              {/* Current Slide Progress */}
              <div className="h-1 bg-black bg-opacity-50">
                <div 
                  className="h-full bg-white transition-all duration-100"
                  style={{ width: `${Math.min(100, progressPercentage)}%` }}
                />
              </div>
            </div>

            {/* Slide Counter & Duration */}
            <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
              {currentSlide + 1}/{storyMedia.length} • {(currentSlideDuration / 1000).toFixed(1)}s
            </div>

            {/* Auto-play indicator */}
            {isPlaying && (
              <div className="absolute top-4 left-4 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                ▶ Playing
              </div>
            )}
          </div>

          {/* Story Description */}
          {story.description && (
            <div className="p-4 border-b border-gray-200">
              <p className="text-gray-700 text-sm leading-relaxed">
                {story.description}
              </p>
            </div>
          )}

          {/* Current Media Info */}
          <div className="p-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">
              {storyMedia[currentSlide]?.title || `Photo ${currentSlide + 1}`}
            </h4>
            {storyMedia[currentSlide]?.description && (
              <p className="text-sm text-gray-600">
                {storyMedia[currentSlide].description}
              </p>
            )}
          </div>

          {/* Story Controls */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={restartStory}
                  className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-200"
                  title="Restart Story"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>

                <button
                  onClick={() => setHasSound(!hasSound)}
                  className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-200"
                  title="Toggle Sound"
                >
                  {hasSound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                
                <button 
                  className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-200"
                  title="Story Settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </div>

              <button 
                onClick={openFullScreen}
                className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1"
              >
                <Maximize2 className="h-3 w-3" />
                Full Screen
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <button
                onClick={togglePlayPause}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  isPlaying 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {isPlaying ? 'Pause' : 'Play'} Story
              </button>
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div className="border-t border-gray-200 p-3 bg-white mt-auto">
            <div className="flex gap-2 overflow-x-auto">
              {storyMedia.map((media, index) => (
                <button
                  key={media.id}
                  onClick={() => goToSlide(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors relative ${
                    index === currentSlide 
                      ? 'border-purple-500' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={media.url || media.thumbnail}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Slide number */}
                  <div className="absolute top-1 left-1 bg-black bg-opacity-60 text-white text-xs rounded px-1">
                    {index + 1}
                  </div>
                  
                  {/* Current slide indicator */}
                  {index === currentSlide && (
                    <div className="absolute inset-0 bg-purple-500 bg-opacity-20 flex items-center justify-center">
                      <Play className="h-4 w-4 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};